import { useCallback, useEffect, useState } from 'react';

/**
 * Shared sidebar collapse state.
 *
 * Persisted under one localStorage key so the collapsed/expanded choice is the
 * SAME across every screen — the main app layout (campaigns / home / settings)
 * and the campaign-detail surface both read and write it, so navigating between
 * them never resets the rail. Owned by the layout (not AppSidebar) so the main
 * content margin can track the rail width.
 */
const SIDEBAR_COLLAPSED_KEY = 'discovr:sidebar-collapsed';
// Below Tailwind `md` the expanded 224px rail leaves too little room for content.
export const NARROW_QUERY = '(max-width: 767px)';

export function isNarrowViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.(NARROW_QUERY).matches === true;
}

/** Narrow screens keep their own choice under a separate key, so a desktop
 *  preference never forces the wide rail onto a phone (or the reverse), and
 *  with no choice yet the rail starts collapsed. */
export function useSidebarCollapse(
  storageKey: string = SIDEBAR_COLLAPSED_KEY,
  { collapseOnNarrow = false }: { collapseOnNarrow?: boolean } = {},
): { collapsed: boolean; toggleCollapsed: () => void } {
  const [narrow, setNarrow] = useState(() => collapseOnNarrow && isNarrowViewport());
  const key = narrow ? `${storageKey}:narrow` : storageKey;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const stored = window.localStorage.getItem(key);
    return stored === null ? narrow : stored === '1';
  });

  // A crossing collapses on the way in and restores the wide preference on the
  // way out; an expand made while narrow lasts only until the next crossing.
  useEffect(() => {
    if (!collapseOnNarrow || typeof window === 'undefined') return;
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(NARROW_QUERY);
    const onChange = (event: { matches: boolean }) => {
      setNarrow(event.matches);
      if (event.matches) {
        window.localStorage.removeItem(`${storageKey}:narrow`);
        setCollapsed(true);
      } else {
        setCollapsed(window.localStorage.getItem(storageKey) === '1');
      }
    };
    // Older Safari's MediaQueryList has addListener only.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    if (typeof mql.addListener === 'function') {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
  }, [collapseOnNarrow, storageKey]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, next ? '1' : '0');
      }
      return next;
    });
  }, [key]);

  return { collapsed, toggleCollapsed };
}

/**
 * Per-group collapse state for the campaign nav's "Outreach"/"Pipeline"
 * accordion sections. Same lazy-read/write-on-toggle localStorage shape as
 * `useSidebarCollapse` above, extended to a JSON blob (one entry per group
 * name) since there's an open-ended set of groups rather than a single flag.
 */
const NAV_GROUP_COLLAPSED_KEY = 'discovr:campaign-nav-groups-collapsed';

function readCollapsedGroups(storageKey: string): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function useNavGroupCollapse(
  storageKey: string = NAV_GROUP_COLLAPSED_KEY,
): { isGroupCollapsed: (group: string) => boolean; toggleGroup: (group: string) => void } {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(() =>
    readCollapsedGroups(storageKey),
  );

  const toggleGroup = useCallback(
    (group: string) => {
      setCollapsedGroups((prev) => {
        const next = { ...prev, [group]: !prev[group] };
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        }
        return next;
      });
    },
    [storageKey],
  );

  const isGroupCollapsed = useCallback((group: string) => Boolean(collapsedGroups[group]), [collapsedGroups]);

  return { isGroupCollapsed, toggleGroup };
}
